import React, { Fragment, cloneElement, useEffect, useRef, useState } from 'react'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import { Route, Link, Routes, useLocation, useNavigate, BrowserRouter } from 'react-router-dom'
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components'
import axios from 'axios'

import { ConditionalRouteType, Slide as SlideType, ThemeProps } from '../../types'
import { ModalProvider, ModalConsumer } from '../../context/ModalContext'
import theme from '../../theme'
import { getKeuzehulp, dvhIsSupported, getPivotLocalStorage } from '../../utils'

import Chapter from '../../components/Chapter'
import CloseAppModal from '../../components/CloseAppModal'
import InfoModal from '../../components/InfoModal'
import Question from '../Question'
import FeedbackQuestion from '../FeedbackQuestion'
import QuizQuestion from '../QuizQuestion'
import Explanation from '../../components/Explanation'
import PopupCards from '../PopupCards'
import Table from '../../components/Table'
import ExplanationWithImage from '../../components/ExplanationWithImage'
import Header from '../../components/Header'
import IntroductionPage from '../../containers/IntroductionPage'
import PrintOverview from '../../containers/PrintOverview'
import Main from '../../components/Main'
import Slide from '../../components/Slide'
import ClosedQuestion from '../ClosedQuestion'
import LikertScale from '../LikertScale'

declare global {
  interface Window {
    _paq: any
  }
}

const KEUZEHULPEN = 'keuzehulpen'

const App: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const mainContentRef = useRef<HTMLDivElement>(null)

  const [closeUrl, setCloseUrl] = useState('')
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [showTopHeader, setshowtopheader] = useState(true)
  const [smallScreen, setSmallScreen] = useState(window.innerHeight < 300)

  const getBasePath = () => {
    const parts = location.pathname.split('/').filter(Boolean)

    // Becomes -1 + 1 === 0 if /keuzehulpen is not in the pathname
    // which happens on localhost during development
    const keuzehulpNameIndex = parts.indexOf(KEUZEHULPEN) + 1

    return `/${parts[keuzehulpNameIndex]}`
  }

  const basePath = getBasePath()
  const { routes, title, documentTitle } = getKeuzehulp(basePath)

  const paths = routes.map((route) => route.path)

  let pathname = location.pathname.replace('/' + KEUZEHULPEN, '')
  if (pathname.slice(-1) === '/') {
    pathname = pathname.slice(0, -1)
  }

  let routeIndex = paths.indexOf(pathname)
  if (pathname.includes('print-overview')) {
    routeIndex = -1
  }

  const routeIsIntroduction = pathname === basePath

  const previousRoute = localStorage.getItem('prevRoute') || basePath
  const previousRouteIndex = paths.indexOf(previousRoute)
  let classNames = routeIndex >= previousRouteIndex ? 'slide' : 'slide-back'

  if (previousRoute === basePath) {
    classNames = 'fade'
  } else if (pathname === basePath) {
    classNames = 'fade-out'
  } else if (location.pathname.includes('/print-overview')) {
    classNames = 'slide'
  } else if (pathname.includes('/print-overview')) {
    classNames = 'slide'
  }

  localStorage.setItem('prevRoute', pathname)

  useEffect(() => {
    const handleResize = () => {
      setSmallScreen(window.innerHeight < 300)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [window?.innerHeight])

  useEffect(() => {
    const getCloseURL = async () => {
      let keuzehulp = location.pathname.split('/')[1]

      if (keuzehulp === 'verken-uw-wensen-voor-zorg-en-behandeling') {
        keuzehulp = 'acp'
      }

      try {
        const { data } = await axios.get(process.env.REACT_APP_API_ENDPOINT || '')
        setCloseUrl((data[keuzehulp] && data[keuzehulp].url) || '')
      } catch (e) {
        return ''
      }
    }

    getCloseURL()
  }, [])

  const storeChapterNumber = () => {
    const chapterPath = location.pathname
    const chapter = chapterPath.split('/')[2]

    if (chapter && chapter.includes('stap-')) {
      const chapterNumber = chapter.split('-')[1]
      const chapterSaved = localStorage.getItem('chapter')

      if (chapterSaved) {
        if (JSON.parse(chapterSaved) < chapterNumber)
          localStorage.setItem('chapter', JSON.stringify(chapterNumber))
      } else {
        localStorage.setItem('chapter', JSON.stringify(chapterNumber))
      }
    }
  }

  const sendLocationChangeToMatomo = () => {
    const { routes, title } = getKeuzehulp(getBasePath())
    const paths = routes.map((route) => route.path)

    let pathname = location.pathname
    if (pathname.slice(-1) === '/') {
      pathname = pathname.slice(0, -1)
    }

    const routeIndex = paths.indexOf(pathname)
    const documentTitle = routes[routeIndex] ? routes[routeIndex].title : title

    const _paq = window._paq || []
    _paq.push(['setCustomUrl', window.location.href])
    _paq.push(['setDocumentTitle', documentTitle])
    _paq.push(['trackPageView'])
    _paq.push(['MediaAnalytics::scanForMedia'])
  }

  const setVh = () => {
    if (!dvhIsSupported()) {
      // Get the viewport height and convert to a value which matches 1vh in pixels.
      const vh = window.innerHeight * 0.01

      // Put the vh value to the custom css property.
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }
  }

  useEffect(() => {
    window.addEventListener('resize', setVh)

    if (location) {
      sendLocationChangeToMatomo()

      if (!showTopHeader) {
        setTimeout(() => {
          setshowtopheader(false)
        }, 300)
      }
    }

    storeChapterNumber()
    setVh()

    return () => {
      window.removeEventListener('resize', setVh)
    }
  }, [location])

  const handleOpenModal = () => {
    setModalIsOpen(true)
  }

  const handleCloseModal = () => {
    setModalIsOpen(false)
  }

  const isPovitPointExist = () => {
    const storage = getPivotLocalStorage('pivotPoints')

    return storage.length > 0
  }

  const getNextSlidePath = (nextRouteId: string, route?: SlideType) => {
    let routeId = nextRouteId
    const { routes } = getKeuzehulp(getBasePath())
    const isPivotPointExist = isPovitPointExist()

    if (isPivotPointExist && route) {
      const pivotPoints = getPivotLocalStorage('pivotPoints')
      const findPivot = pivotPoints.find((pivot: ConditionalRouteType) => {
        return pivot.pivotRouteId === route.routeId
      })

      if (findPivot) {
        routeId = findPivot.nextRouteId
      }
    }

    const nextRoute = routes.find((route: SlideType) => route.routeId === routeId)

    return nextRoute ? nextRoute.path : ''
  }

  const skipToMain = () => {
    if (mainContentRef && mainContentRef.current) {
      mainContentRef.current.focus()
    }
  }

  useEffect(() => {
    // Scroll to top when the route changes
    window.scrollTo(0, 0)
  }, [location])

  const handleSkipRoute = (path: string) => {
    try {
      navigate(path, { replace: true })
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <Wrapper $smallScreen={smallScreen}>
      <SkipLink
        tabIndex={0}
        onClick={skipToMain}
        onKeyDown={(e) => {
          if (e.keyCode === 13) {
            skipToMain()
          }
        }}
      >
        Overslaan en naar de inhoud gaan
      </SkipLink>
      <Header
        activeRouteIndex={routeIndex}
        routes={routes}
        baseName={documentTitle}
        routeIsIntroduction={routeIsIntroduction}
        onModalButtonClick={handleOpenModal}
        modalIsOpen={modalIsOpen}
        theme={theme}
      />
      <Main ref={mainContentRef} tabIndex={-1}>
        <TransitionGroup
          childFactory={(child) => cloneElement(child, { classNames })}
          appear={true}
        >
          <CSSTransition key={location.key} classNames={classNames} timeout={600} unmountOnExit>
            <Routes location={location}>
              <Route
                key="print-overview"
                path={`${basePath}/print-overview`}
                element={
                  <PrintOverview
                    appURL={`keuzehulpen.thuisarts.nl/${basePath}`}
                    routes={routes}
                    startLink={basePath}
                    backLink={
                      basePath === '/psa'
                        ? 'https://www.thuisarts.nl/prostaatkanker'
                        : 'https://www.thuisarts.nl'
                    }
                    title={title}
                  />
                }
              />

              {routes.map((route: SlideType) => {
                const nextLink = getNextSlidePath(route.nextRouteId, route)

                if (route.type === 'intro')
                  return (
                    <Route
                      key={route.routeId}
                      path={route.path}
                      element={
                        <IntroductionPage
                          title={route.title || ''}
                          columns={route.columns || []}
                          nextLink={nextLink}
                          backButton={route.backButton}
                        />
                      }
                    />
                  )
                else if (route.type === 'chapter')
                  return (
                    <Route
                      key={route.routeId}
                      path={route.path}
                      element={
                        <Slide>
                          <Chapter
                            routeIndex={route.routeIndex}
                            routes={routes}
                            title={route.title || ''}
                            subtitle={route.subtitle || ''}
                            description={route.description || ''}
                            image={route.image}
                            button={route.button || ''}
                            nextLink={nextLink}
                            onSkipRoute={handleSkipRoute}
                          />
                        </Slide>
                      }
                    />
                  )
                else if (route.type === 'question')
                  return (
                    <Route
                      key={route.routeId}
                      path={route.path}
                      element={
                        <Slide>
                          <Question
                            routeId={route.routeId}
                            basePath={basePath}
                            button={route.button || ''}
                            description={route.description || ''}
                            descriptionAlign={route.descriptionAlign || ''}
                            questions={route.questions || []}
                            subtitle={route.subtitle || ''}
                            title={route.title || ''}
                            showTextArea={route.showTextArea}
                            textAreaLabel={route.textAreaLabel}
                            nextLink={nextLink}
                            routeIndex={route.routeIndex}
                            routes={routes}
                            modalHeader={route.modalHeader || ''}
                            modalLink={route.modalLink || ''}
                            modalText={route.modalText || ''}
                            modalLinks={route.modalLinks}
                            onSkipRoute={handleSkipRoute}
                          />
                        </Slide>
                      }
                    />
                  )
                else if (
                  route.type === 'statement' ||
                  route.type === 'defaultQuestion' ||
                  route.type === 'uniqueQuestion'
                )
                  return (
                    <Route
                      key={route.routeId}
                      path={route.path}
                      element={
                        <Slide>
                          <ClosedQuestion
                            routeId={route.routeId}
                            basePath={basePath}
                            layout={route.layout || 'horizontal'}
                            answers={route.answers || []}
                            description={route.description || ''}
                            info={route.info}
                            subtitle={route.subtitle || ''}
                            title={route.title || ''}
                            type={route.type || ''}
                            nextLink={nextLink}
                            getNextSlidePath={getNextSlidePath}
                            routeIndex={route.routeIndex}
                            routes={routes}
                            onSkipRoute={handleSkipRoute}
                          />
                        </Slide>
                      }
                    />
                  )
                else if (route.type === 'likertScale')
                  return (
                    <Route
                      key={route.routeId}
                      path={route.path}
                      element={
                        <Slide>
                          <LikertScale
                            routeId={route.routeId}
                            basePath={basePath}
                            subtitle={route.subtitle || ''}
                            customAnswers={route.answers}
                            title={route.title || ''}
                            nextLink={nextLink}
                            button={route.button || ''}
                            routeIndex={route.routeIndex}
                            routes={routes}
                            onSkipRoute={handleSkipRoute}
                          />
                        </Slide>
                      }
                    />
                  )
                else if (route.type === 'feedbackQuestion')
                  return (
                    <Route
                      key={route.routeId}
                      path={route.path}
                      element={
                        <Slide>
                          <FeedbackQuestion
                            routeId={route.routeId}
                            basePath={basePath}
                            subtitle={route.subtitle || ''}
                            description={route.description || ''}
                            descriptionAlign={route.descriptionAlign || ''}
                            feedbackAnswers={route.feedbackAnswers || []}
                            title={route.title || ''}
                            nextLink={nextLink}
                            button={route.button || ''}
                            routeIndex={route.routeIndex}
                            routes={routes}
                            onSkipRoute={handleSkipRoute}
                          />
                        </Slide>
                      }
                    />
                  )
                else if (route.type === 'quizQuestion')
                  return (
                    <Route
                      key={route.routeId}
                      path={route.path}
                      element={
                        <Slide>
                          <QuizQuestion
                            routeId={route.routeId}
                            basePath={basePath}
                            subtitle={route.subtitle || ''}
                            title={route.title || ''}
                            quizAnswers={route.quizAnswers || []}
                            correctQuizAnswer={route.correctQuizAnswer || ''}
                            explanation={route.explanation || ''}
                            nextLink={nextLink}
                            modalLink={route.modalLink || ''}
                            modalHeader={route.modalHeader || ''}
                            modalText={route.modalText || ''}
                            button={route.button || ''}
                            routeIndex={route.routeIndex}
                            routes={routes}
                            onSkipRoute={handleSkipRoute}
                          />
                        </Slide>
                      }
                    />
                  )
                else if (route.type === 'explanation')
                  return (
                    <Route
                      key={route.routeId}
                      path={route.path}
                      element={
                        <Slide>
                          <Explanation
                            title={route.title || ''}
                            description={route.description || ''}
                            align={route.align}
                            titleAlign={route.titleAlign}
                            button={route.button || ''}
                            videos={route.videos}
                            image={route.image}
                            info={route.info}
                            nextLink={nextLink}
                            modalLink={route.modalLink || ''}
                            modalHeader={route.modalHeader || ''}
                            modalText={route.modalText || ''}
                            modalLinks={route.modalLinks}
                            skipSteps={route.skipSteps}
                            routeIndex={route.routeIndex}
                            routes={routes}
                            onSkipRoute={handleSkipRoute}
                          />
                        </Slide>
                      }
                    />
                  )
                else if (route.type === 'table')
                  return (
                    <Route
                      key={route.routeId}
                      path={route.path}
                      element={
                        <Slide>
                          <Table
                            title={route.title || ''}
                            contentTable={route.contentTable || []}
                            button={route.button || ''}
                            nextLink={nextLink}
                            tableHeader={route.tableHeader || []}
                            routeIndex={route.routeIndex}
                            routes={routes}
                            onSkipRoute={handleSkipRoute}
                          />
                        </Slide>
                      }
                    />
                  )
                else if (route.type === 'explanationWithImage')
                  return (
                    <Route
                      key={route.routeId}
                      path={route.path}
                      element={
                        <Slide>
                          <ExplanationWithImage
                            title={route.title || ''}
                            contentImage={route.contentImage || []}
                            align={route.align}
                            button={route.button || ''}
                            nextLink={nextLink}
                            routeIndex={route.routeIndex}
                            routes={routes}
                            onSkipRoute={handleSkipRoute}
                          />
                        </Slide>
                      }
                    />
                  )
                else if (route.type === 'popupCards')
                  return (
                    <Route
                      key={route.routeId}
                      path={route.path}
                      element={
                        <Slide>
                          <PopupCards
                            title={route.title || ''}
                            align={route.align}
                            description={route.description || ''}
                            cards={route.cards || []}
                            button={route.button || ''}
                            nextLink={nextLink}
                            routeIndex={route.routeIndex}
                            routes={routes}
                            routeId={route.routeId}
                            onSkipRoute={handleSkipRoute}
                          />
                        </Slide>
                      }
                    />
                  )
                else if (route.type === 'end')
                  return <Route key={route.type} path={route.path} element={<div>END</div>} />
                else
                  return (
                    <Slide>
                      <TestSlide>{nextLink && <Link to={nextLink}>Volgende</Link>}</TestSlide>
                    </Slide>
                  )
              })}
              <Route
                path="/"
                key="thuisarts"
                Component={() => {
                  window.location.assign('https://www.thuisarts.nl')
                  return <NoMatch />
                }}
              />
            </Routes>
          </CSSTransition>
        </TransitionGroup>
      </Main>
      <ModalConsumer>
        {({ closeModal, modalIsOpen, title, content, videos }) => (
          <InfoModal
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            title={title}
            content={content}
            videos={videos}
          />
        )}
      </ModalConsumer>
      <CloseAppModal handleCloseModal={handleCloseModal} modalIsOpen={modalIsOpen} url={closeUrl} />
    </Wrapper>
  )
}

const GlobalStyle = createGlobalStyle<ThemeProps>`
  body {
    color: ${({ theme }) => theme.textColor};
  }
`

const SkipLink = styled.button`
  position: absolute;
  transform: translateY(-100%);
  z-index: 99999;
  left: 50%;
  background: transparent;
  outline: none;
  border: none;
  width: 1px;
  height: 1px;
  overflow: hidden;
  color: ${theme.darkGrey};
  font-size: 1.125rem;

  &:focus {
    transform: translate(-50%, 0);
    width: auto;
    height: auto;
  }
`

const TestSlide = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`

const AppProvider: React.FC = () => (
  <ThemeProvider theme={theme}>
    <ModalProvider>
      <Fragment>
        <GlobalStyle theme={theme} />
        <BrowserRouter basename="/keuzehulpen">
          <Routes>
            <Route element={<App />} path="/*" />
          </Routes>
        </BrowserRouter>
      </Fragment>
    </ModalProvider>
  </ThemeProvider>
)

const Wrapper = styled.div<{ $smallScreen: boolean }>`
  display: flex;
  flex-direction: column;
  height: calc(var(--vh) * 100);
  width: 100%;

  ${({ $smallScreen }) =>
    $smallScreen &&
    `
    height: 100%;
  `}
`

const NoMatch = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: white;
`

export default AppProvider
